import { useEffect, useRef, useState } from 'react';
import { ComicBoard } from './ComicBoard';
import { StoryText } from './StoryText';
import { useFollowScroll } from '../lib/followScroll';
import type { ClientMessage } from '../../shared/protocol';
import type { AuthorSeat, ComicDef, PublicRound } from '../../shared/types';

/**
 * The author's writing surface. Deliberately NOT a text input: it is a
 * focusable region that captures raw keydown events and relays them to the
 * server, and renders the authoritative text that comes back. Because no
 * editable element exists, every removal path — Backspace, Delete, cut,
 * paste-over, undo, drag, selection-replace — is impossible by construction
 * (decision B3/F-13/F-14), the caret is inherently locked to the end (F-15),
 * and IME/autocorrect composition never reaches the text (decision B4).
 *
 * Key mapping (B1 revised 2026-07-18 — authors write Enter-for-newline as
 * they are used to; the deliberate two-key chord passes the turn):
 *   Enter        → line break
 *   Shift+Enter  → pass the turn
 *   Tab          → disabled (logged)
 *   printable    → append character
 *
 * In a Bildrunde (2026-09-08) the same surface renders the comic board
 * instead of the flowing story: the characters land in the caption slot of
 * the panel currently in play, and Shift+Enter closes that panel and hands
 * the next one to the other author.
 */
export function WritingSurface(props: {
  round: PublicRound;
  authors: AuthorSeat[];
  authorIndex: number;
  send: (msg: ClientMessage) => void;
  /** Bildrunde: the comic being captioned, plus where to fetch its bytes. */
  comic?: { def: ComicDef; src: string } | null;
}) {
  const { round, authors, authorIndex, send, comic } = props;
  const ref = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);
  const myTurn = round.phase === 'running' && round.activeAuthor === authorIndex;

  // Story mode follows the newest line. A Bildrunde has no "newest line" —
  // the board jumps between panel slots, so the board scrolls itself.
  useFollowScroll(ref, round.kind === 'comic' ? 0 : round.text.length);

  // The surface grabs focus whenever the turn arrives — the author must be
  // able to type immediately, no click required (N-6: no fumbling on stage).
  useEffect(() => {
    if (myTurn) ref.current?.focus();
  }, [myTurn]);

  const blocked = (key: string) => send({ type: 'key', kind: 'blocked', key });

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.nativeEvent.isComposing) {
      e.preventDefault(); // B4: composition/autocorrect input is ignored
      return;
    }
    const k = e.key;
    if (k === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) send({ type: 'key', kind: 'enter' });
      else send({ type: 'key', kind: 'char', key: 'Enter', char: '\n' });
      return;
    }
    if (k === 'Tab') {
      e.preventDefault(); // B2: Tab is disabled entirely
      blocked('Tab');
      return;
    }
    if (k === 'Backspace' || k === 'Delete') {
      e.preventDefault(); // B3: no deleting, ever
      blocked(k);
      return;
    }
    if (e.metaKey || e.ctrlKey) {
      e.preventDefault(); // B3: swallow paste/cut/undo/redo and friends
      if (/^[vxzya]$/i.test(k)) blocked(`${e.metaKey ? 'Meta' : 'Ctrl'}+${k.toLowerCase()}`);
      return;
    }
    if (k.length === 1) {
      e.preventDefault();
      send({ type: 'key', kind: 'char', key: k, char: k });
    }
    // Everything else (arrows, Home/End, F-keys): the caret is locked to the
    // end and the browser has nothing to move — intentionally inert.
  };

  const swallow = (e: React.SyntheticEvent) => e.preventDefault();

  return (
    <div
      ref={ref}
      className={`surface ${round.kind === 'comic' ? 'surface--comic comicfit' : ''} ${
        myTurn ? 'surface--live' : 'surface--waiting'
      } ${focused ? 'surface--focused' : ''}`}
      tabIndex={0}
      role="textbox"
      aria-label="Schreibfläche"
      aria-readonly={!myTurn}
      onKeyDown={onKeyDown}
      onPaste={swallow}
      onCut={swallow}
      onDrop={swallow}
      onDragOver={swallow}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      data-testid="writing-surface"
    >
      {round.kind === 'comic' && comic ? (
        <ComicBoard
          comic={comic.def}
          src={comic.src}
          round={round}
          authors={authors}
          showCaret
          className="comicboard--surface"
        />
      ) : (
        <StoryText round={round} authors={authors} showCaret={myTurn} />
      )}
      {!focused && myTurn && <div className="surface__hint">Klicken oder tippen — du bist dran</div>}
    </div>
  );
}
